import React, { useCallback, useEffect, useState } from "react";
import { Printer } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import {
  APTITUDE_A_META,
  APTITUDE_B_META,
  APTITUDE_C_META,
  APTITUDE_D_META,
} from "../screening/aptitudeReviewContent";
import { API_BASE_URL } from "../../config";

const API = API_BASE_URL;

interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    username: string;
    email: string;
    userId: string;
    role: string;
  };
}

interface MarksData {
  id: string;
  totalMarks: number;
  logicalTotal: number;
  memorizationTotal: number;
  aptitudeTotal: number;
  name: string;
  phoneNo: string;
  education: string;
}

interface MarksResponse {
  success: boolean;
  message: string;
  data: MarksData;
}

type QuestionItem = {
  question?: string;
  options?: string[];
  marks?: number;
  correctAnswer?: string | string[];
  selectedAnswer?: string | string[] | null;
  questionType?: string;
  image?: string;
};

type AptitudeBlock = {
  correctAnswer?: Record<string, string>;
  selectedAnswer?: Record<string, string>;
  marks?: number;
  questionType?: string;
};

type ScreeningRecord = Record<string, unknown>;

/** Rounds fractional marks for display (matches API float noise). */
function formatMarksDisplay(m: number): number {
  return Math.round(m * 100) / 100;
}

function formatAnswer(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    return value.map((v) => String(v)).join(", ");
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  const s = String(value).trim();
  return s || "—";
}

function normalizeText(s: string): string {
  return s.trim().toLowerCase();
}

/** Whether `value` matches any of correct (string or acceptable variants). */
function matchesCorrect(
  value: string,
  correct: string | string[] | undefined,
): boolean {
  if (correct === undefined) return false;
  const list = Array.isArray(correct) ? correct : [correct];
  return list.some((c) => normalizeText(String(c)) === normalizeText(value));
}

/** Selected answer vs options / free text (MCQ: one string; multi: array). */
function matchesSelected(
  optionText: string,
  selected: string | string[] | null | undefined,
): boolean {
  if (selected === null || selected === undefined) return false;
  if (Array.isArray(selected)) {
    return selected.some(
      (s) => normalizeText(String(s)) === normalizeText(optionText),
    );
  }
  return normalizeText(String(selected)) === normalizeText(optionText);
}

function hasSelection(selected: string | string[] | null | undefined): boolean {
  if (selected === null || selected === undefined) return false;
  if (Array.isArray(selected)) return selected.length > 0;
  return String(selected).trim() !== "";
}

function answersMatch(
  selected: string | string[] | undefined | null,
  correct: string | string[] | undefined | null,
): boolean | null {
  if (correct === undefined || correct === null) return null;
  if (
    selected === undefined ||
    selected === null ||
    (Array.isArray(selected) ? selected.length === 0 : String(selected).trim() === "")
  )
    return false;

  const selList = Array.isArray(selected) ? selected : [selected];
  const corList = Array.isArray(correct) ? correct : [correct];

  return corList.some((c) =>
    selList.some((s) => normalizeText(String(s)) === normalizeText(String(c)))
  );
}

const PlacementResult: React.FC = () => {
  const [searchParams] = useSearchParams();
  const candidateId = searchParams.get("candidateId");

  const [authLoading, setAuthLoading] = useState(true);
  const [userName, setUserName] = useState<string | null>(null);

  const [marks, setMarks] = useState<MarksData | null>(null);
  const [screening, setScreening] = useState<ScreeningRecord | null>(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redirectToLogin = () => {
    window.location.href = "/placement/login";
  };

  const performLogout = useCallback(() => {
    localStorage.removeItem("userId");
    localStorage.removeItem("userToken");
    redirectToLogin();
  }, []);

  useEffect(() => {
    const run = async () => {
      try {
        setAuthLoading(true);
        const userId = localStorage.getItem("userId");
        if (!userId) {
          redirectToLogin();
          return;
        }
        const res = await fetch(`${API}/login/user/${userId}`);
        if (!res.ok) throw new Error("Failed to verify session");
        const result: LoginResponse = await res.json();
        if (!result.success || !result.data) {
          throw new Error(result.message || "Unauthorized");
        }
        if (result.data.role.toLowerCase() !== "placement") {
          alert("Access denied. Only placement can view this page.");
          performLogout();
          return;
        }
        setUserName(result.data.username);
      } catch {
        alert("Authentication failed. Please login again.");
        performLogout();
      } finally {
        setAuthLoading(false);
      }
    };
    run();
  }, [performLogout]);

  useEffect(() => {
    if (authLoading || !candidateId) return;

    const load = async () => {
      setDataLoading(true);
      setError(null);
      try {
        const [qRes, mRes] = await Promise.all([
          fetch(`${API}/screening-questions/${candidateId}`),
          fetch(`${API}/screening-questions/${candidateId}/marks`),
        ]);

        if (!qRes.ok) {
          throw new Error("Could not load screening answers.");
        }
        const qJson: unknown = await qRes.json();
        const row = Array.isArray(qJson) && qJson.length > 0 ? qJson[0] : null;
        if (!row || typeof row !== "object") {
          throw new Error("No screening data for this candidate.");
        }
        setScreening(row as ScreeningRecord);

        if (mRes.ok) {
          const mJson: MarksResponse = await mRes.json();
          if (mJson.success && mJson.data) {
            setMarks(mJson.data);
          }
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load results.");
      } finally {
        setDataLoading(false);
      }
    };

    load();
  }, [authLoading, candidateId]);

  useEffect(() => {
    if (!marks?.name) return;
    const previous = document.title;
    document.title = `Screening result — ${marks.name}`;
    return () => {
      document.title = previous;
    };
  }, [marks?.name]);

  const renderQuestionRow = (item: QuestionItem, idx: number) => {
    const key = `q-${idx}`;
    const qType = item.questionType ?? "";
    const options = item.options ?? [];
    const hasOpts = options.length > 0;
    const isMultiChoice =
      hasOpts &&
      (qType === "multiple-choice" ||
        qType === "multipleChoice" ||
        (!qType && hasOpts));

    const selected = item.selectedAnswer;
    const correct = item.correctAnswer;

    const summarySelected = formatAnswer(selected);
    const summaryCorrect = formatAnswer(correct);

    return (
      <div
        key={key}
        className="rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50/90 p-4 shadow-sm sm:p-6 print:break-inside-avoid print:shadow-none"
      >
        <div className="flex gap-3">
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-sm font-bold text-white"
            aria-hidden
          >
            {idx + 1}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold leading-snug text-slate-900 whitespace-pre-wrap">
              {item.question ?? "(No question text)"}
            </p>
            {qType && (
              <p className="mt-1 text-xs font-medium text-slate-500">
                Type:{" "}
                <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-slate-700">
                  {qType}
                </span>
              </p>
            )}
          </div>
        </div>

        {item.image && (
          <img
            src={item.image}
            alt=""
            className="mt-4 max-h-72 w-full max-w-2xl rounded-xl border border-slate-200 bg-white object-contain p-2"
          />
        )}

        {/* Multiple choice: list options with correct / selected highlights */}
        {isMultiChoice && (
          <div className="mt-5 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Options
            </p>
            <ul className="space-y-2">
              {options.map((opt, oi) => {
                const letter = String.fromCharCode(65 + oi);
                const isCor = matchesCorrect(opt, correct);
                const isSel = matchesSelected(opt, selected);
                return (
                  <li
                    key={`${key}-opt-${oi}`}
                    className={`flex flex-wrap items-start gap-2 rounded-xl border px-3 py-2.5 text-sm sm:px-4 sm:py-3 ${
                      isCor && isSel
                        ? "border-emerald-400 bg-emerald-50/90 ring-1 ring-emerald-200"
                        : isCor
                          ? "border-emerald-300 bg-emerald-50/60"
                          : isSel
                            ? "border-rose-300 bg-rose-50/80 ring-1 ring-rose-200"
                            : "border-slate-100 bg-white"
                    }`}
                  >
                    <span className="mt-0.5 font-mono text-xs font-bold text-slate-500">
                      {letter}.
                    </span>
                    <span className="min-w-0 flex-1 font-medium text-slate-800">
                      {opt}
                    </span>
                    <span className="flex flex-wrap gap-1.5">
                      {isCor && (
                        <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-semibold text-white">
                          Correct answer
                        </span>
                      )}
                      {isSel && (
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-semibold text-white ${
                            isCor ? "bg-sky-600" : "bg-rose-600"
                          }`}
                        >
                          {isCor ? "Your answer ✓" : "Your answer ✗"}
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
            {!hasSelection(selected) && (
              <p className="text-sm text-amber-800">
                <span className="font-semibold">Not answered.</span> The correct
                option is marked above.
              </p>
            )}
          </div>
        )}

        {/* Fill-in (no options) or multi-word recall */}
        {!isMultiChoice && (
          <div className="mt-5 space-y-4">
            {hasOpts && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Reference / choices
                </p>
                <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-slate-700">
                  {options.map((opt, oi) => (
                    <li key={`${key}-ref-${oi}`}>{opt}</li>
                  ))}
                </ul>
              </div>
            )}

            {qType === "fillInMultiple" ||
            (Array.isArray(correct) && Array.isArray(selected)) ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-sky-200 bg-sky-50/80 p-4 ring-1 ring-sky-100">
                  <p className="text-xs font-bold uppercase tracking-wide text-sky-800">
                    Candidate wrote (selected)
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {Array.isArray(selected) && selected.length > 0 ? (
                      selected.map((w, wi) => (
                        <span
                          key={`${key}-s-${wi}`}
                          className="rounded-lg bg-white px-2.5 py-1 text-sm font-medium text-slate-800 ring-1 ring-sky-200"
                        >
                          {String(w)}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-slate-500">—</span>
                    )}
                  </div>
                </div>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-4 ring-1 ring-emerald-100">
                  <p className="text-xs font-bold uppercase tracking-wide text-emerald-800">
                    Correct answers
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(Array.isArray(correct) ? correct : [correct]).map(
                      (w, wi) => (
                        <span
                          key={`${key}-c-${wi}`}
                          className="rounded-lg bg-white px-2.5 py-1 text-sm font-medium text-emerald-900 ring-1 ring-emerald-200"
                        >
                          {String(w)}
                        </span>
                      ),
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-sky-200 bg-sky-50/80 p-4 ring-1 ring-sky-100">
                  <p className="text-xs font-bold uppercase tracking-wide text-sky-800">
                    Selected answer
                  </p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">
                    {summarySelected}
                  </p>
                </div>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-4 ring-1 ring-emerald-100">
                  <p className="text-xs font-bold uppercase tracking-wide text-emerald-800">
                    Correct answer
                  </p>
                  <p className="mt-2 text-sm font-semibold text-emerald-950">
                    {summaryCorrect}
                  </p>
                  {Array.isArray(correct) && correct.length > 1 && (
                    <p className="mt-1 text-xs text-emerald-800">
                      (Any of these values is accepted.)
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {typeof item.marks === "number" && (
          <p className="mt-4 border-t border-slate-100 pt-3 text-xs font-medium text-slate-500">
            Marks earned:{" "}
            <span className="text-slate-800">
              {formatMarksDisplay(item.marks)}
            </span>
          </p>
        )}
      </div>
    );
  };

  const renderAptitudeSuite = (s: ScreeningRecord) => {
    const a = s.aptitude_A as AptitudeBlock | undefined;
    const b = s.aptitude_B as AptitudeBlock | undefined;
    const c = s.aptitude_C as AptitudeBlock | undefined;
    const d = s.aptitude_D as AptitudeBlock | undefined;

    if (!a && !b && !c && !d) return null;

    const sectionShell = (children: React.ReactNode) => (
      <section className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-xl shadow-slate-200/40 print:overflow-visible print:shadow-none">
        <div className="space-y-6 p-4 sm:p-8">{children}</div>
      </section>
    );

    const calc = (
      <div key="calc" className="">
        <h3 className="text-slate-900 text-lg font-bold  sm:text-xl">
          {APTITUDE_A_META.title}
        </h3>
        <div className="mt-6 space-y-6">
          {APTITUDE_A_META.items.map((item, idx) => {
            const sel = a?.selectedAnswer?.[item.key];
            const cor = a?.correctAnswer?.[item.key];
            const ok = answersMatch(sel, cor);
            return (
              <div
                key={item.key}
                className="rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50/90 p-4 shadow-sm sm:p-6"
              >
                <div className="flex gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-sm font-bold text-white">
                    {idx + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-semibold leading-snug text-slate-900">
                      {item.question}
                    </p>
                    <p className="mt-2 rounded-lg bg-slate-100 px-3 py-2 font-mono text-sm text-slate-800">
                      {item.label}
                    </p>
                    <p className="mt-1 text-xs font-medium text-slate-500">
                      Type:{" "}
                      <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-slate-700">
                        calculation
                      </span>{" "}
                      · No A/B/C options — type the number only.
                    </p>
                  </div>
                </div>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-xl border border-sky-200 bg-sky-50/80 p-4 ring-1 ring-sky-100">
                    <p className="text-xs font-bold uppercase tracking-wide text-sky-800">
                      Selected answer
                    </p>
                    <p className="mt-2 text-lg font-semibold tabular-nums text-slate-900">
                      {formatAnswer(sel)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-4 ring-1 ring-emerald-100">
                    <p className="text-xs font-bold uppercase tracking-wide text-emerald-800">
                      Correct answer
                    </p>
                    <p className="mt-2 text-lg font-semibold tabular-nums text-emerald-950">
                      {formatAnswer(cor)}
                    </p>
                  </div>
                </div>
                {ok !== null && (
                  <p
                    className={`mt-3 text-sm font-semibold ${
                      ok ? "text-emerald-600" : "text-rose-600"
                    }`}
                  >
                    {ok ? "Correct" : "Incorrect"}
                  </p>
                )}
              </div>
            );
          })}
        </div>
        {typeof a?.marks === "number" && (
          <p className="mt-4 text-sm text-slate-600">
            Section marks:{" "}
            <span className="font-semibold text-slate-900">
              {formatMarksDisplay(a.marks)}
            </span>
          </p>
        )}
      </div>
    );

    const matching = (
      <div key="match" className="">
        <h3 className="text-lg font-bold text-slate-900 sm:text-xl">
          {APTITUDE_B_META.title}
        </h3>
        <div className="mt-6 space-y-8">
          {APTITUDE_B_META.rows.map((row) => {
            const sel = b?.selectedAnswer?.[row.key];
            const cor = b?.correctAnswer?.[row.key];
            const selLetter = sel ? String(sel).trim().toUpperCase() : "";
            const corLetter = cor ? String(cor).trim().toUpperCase() : "";
            const ok = answersMatch(
              selLetter || undefined,
              corLetter || undefined,
            );
            const letters = ["A", "B", "C", "D"] as const;
            return (
              <div
                key={row.key}
                className="rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50/90 p-4 shadow-sm sm:p-6"
              >
                <div className="flex gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-sm font-bold text-white">
                    {row.no}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-semibold text-slate-900">
                      {row.question}
                    </p>
                    <p className="mt-1 text-xs font-medium text-slate-500">
                      Type:{" "}
                      <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-slate-700">
                        matching
                      </span>
                    </p>
                  </div>
                </div>

                <div className="mt-5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Question figure (match one of A–D)
                  </p>
                  <div className="mt-2 flex justify-center rounded-xl border border-slate-200 bg-white p-4 print:p-2">
                    <img
                      src={row.questionImg}
                      alt={`Question ${row.no}`}
                      className="aptitude-match-q-img max-h-32 object-contain sm:max-h-40"
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Options
                  </p>
                  <ul className="mt-3 space-y-3">
                    {letters.map((L) => {
                      const isCor = corLetter === L;
                      const isSel = selLetter === L;
                      return (
                        <li
                          key={L}
                          className={`flex flex-col gap-2 rounded-xl border px-3 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-4 ${
                            isCor && isSel
                              ? "border-emerald-400 bg-emerald-50/90 ring-1 ring-emerald-200"
                              : isCor
                                ? "border-emerald-300 bg-emerald-50/60"
                                : isSel
                                  ? "border-rose-300 bg-rose-50/80 ring-1 ring-rose-200"
                                  : "border-slate-100 bg-white"
                          }`}
                        >
                          <div className="flex min-w-0 flex-1 items-center gap-3">
                            <span className="font-mono text-sm font-bold text-slate-500">
                              {L}.
                            </span>
                            <img
                              src={row.options[L]}
                              alt={`Option ${L}`}
                              className="aptitude-match-opt-img max-h-16 w-auto object-contain sm:max-h-20"
                            />
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {isCor && (
                              <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-semibold text-white">
                                Correct answer
                              </span>
                            )}
                            {isSel && (
                              <span
                                className={`rounded-full px-2 py-0.5 text-xs font-semibold text-white ${
                                  isCor ? "bg-sky-600" : "bg-rose-600"
                                }`}
                              >
                                {isCor ? "Your answer ✓" : "Your answer ✗"}
                              </span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="mt-5 grid gap-4 border-t border-slate-100 pt-4 sm:grid-cols-2">
                  <div className="rounded-xl border border-sky-200 bg-sky-50/80 p-3 ring-1 ring-sky-100">
                    <p className="text-xs font-bold uppercase tracking-wide text-sky-800">
                      Selected answer
                    </p>
                    <p className="mt-1 text-xl font-bold tabular-nums text-slate-900">
                      {selLetter || "—"}
                    </p>
                  </div>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-3 ring-1 ring-emerald-100">
                    <p className="text-xs font-bold uppercase tracking-wide text-emerald-800">
                      Correct answer
                    </p>
                    <p className="mt-1 text-xl font-bold tabular-nums text-emerald-950">
                      {corLetter || "—"}
                    </p>
                  </div>
                </div>
                {!selLetter && (
                  <p className="mt-3 text-sm text-amber-800">
                    <span className="font-semibold">Not answered.</span> The
                    correct option is marked above.
                  </p>
                )}
                {ok !== null && (
                  <p
                    className={`mt-2 text-sm font-semibold ${
                      ok ? "text-emerald-600" : "text-rose-600"
                    }`}
                  >
                    {ok ? "Correct" : "Incorrect"}
                  </p>
                )}
              </div>
            );
          })}
        </div>
        {typeof b?.marks === "number" && (
          <p className="mt-4 text-sm text-slate-600">
            Section marks:{" "}
            <span className="font-semibold text-slate-900">
              {formatMarksDisplay(b.marks)}
            </span>
          </p>
        )}
      </div>
    );

    const iq = (
      <div key="iq" className="">
        <h3 className="text-lg font-bold text-slate-900 sm:text-xl">
          {APTITUDE_C_META.title}
        </h3>
        <div className="mt-6 space-y-6">
          {APTITUDE_C_META.items.map((item, idx) => {
            const sel = c?.selectedAnswer?.[item.key];
            const cor = c?.correctAnswer?.[item.key];
            const ok = answersMatch(sel, cor);
            return (
              <div
                key={item.key}
                className="rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50/90 p-4 shadow-sm sm:p-6"
              >
                <div className="flex gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-sm font-bold text-white">
                    {idx + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-semibold leading-snug text-slate-900">
                      {item.prompt}
                    </p>
                    <p className="mt-1 text-xs font-medium text-slate-500">
                      Type:{" "}
                      <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-slate-700">
                        iq
                      </span>
                    </p>
                  </div>
                </div>
                {"image" in item && item.image ? (
                  <img
                    src={item.image}
                    alt=""
                    className="mt-4 max-w-md rounded-xl border border-slate-200 bg-white object-contain p-2"
                  />
                ) : null}

                <div className="mt-5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Options
                  </p>
                  <ul className="mt-3 space-y-2">
                    {item.choices.map((choice, oi) => {
                      const letter = String.fromCharCode(65 + oi);
                      const isCor = matchesCorrect(choice, cor);
                      const isSel = matchesSelected(choice, sel);
                      return (
                        <li
                          key={`${item.key}-${choice}`}
                          className={`flex flex-wrap items-start gap-2 rounded-xl border px-3 py-2.5 text-sm sm:px-4 sm:py-3 ${
                            isCor && isSel
                              ? "border-emerald-400 bg-emerald-50/90 ring-1 ring-emerald-200"
                              : isCor
                                ? "border-emerald-300 bg-emerald-50/60"
                                : isSel
                                  ? "border-rose-300 bg-rose-50/80 ring-1 ring-rose-200"
                                  : "border-slate-100 bg-white"
                          }`}
                        >
                          <span className="mt-0.5 font-mono text-xs font-bold text-slate-500">
                            {letter}.
                          </span>
                          <span className="min-w-0 flex-1 font-medium text-slate-800">
                            {choice}
                          </span>
                          <span className="flex flex-wrap gap-1.5">
                            {isCor && (
                              <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-semibold text-white">
                                Correct answer
                              </span>
                            )}
                            {isSel && (
                              <span
                                className={`rounded-full px-2 py-0.5 text-xs font-semibold text-white ${
                                  isCor ? "bg-sky-600" : "bg-rose-600"
                                }`}
                              >
                                {isCor ? "Your answer ✓" : "Your answer ✗"}
                              </span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="mt-5 grid gap-4 border-t border-slate-100 pt-4 sm:grid-cols-2">
                  <div className="rounded-xl border border-sky-200 bg-sky-50/80 p-3 ring-1 ring-sky-100">
                    <p className="text-xs font-bold uppercase tracking-wide text-sky-800">
                      Selected answer
                    </p>
                    <p className="mt-1 text-lg font-semibold text-slate-900">
                      {formatAnswer(sel)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-3 ring-1 ring-emerald-100">
                    <p className="text-xs font-bold uppercase tracking-wide text-emerald-800">
                      Correct answer
                    </p>
                    <p className="mt-1 text-lg font-semibold text-emerald-950">
                      {formatAnswer(cor)}
                    </p>
                  </div>
                </div>
                {!hasSelection(sel) && (
                  <p className="mt-3 text-sm text-amber-800">
                    <span className="font-semibold">Not answered.</span> The
                    correct option is marked above.
                  </p>
                )}
                {ok !== null && (
                  <p
                    className={`mt-2 text-sm font-semibold ${
                      ok ? "text-emerald-600" : "text-rose-600"
                    }`}
                  >
                    {ok ? "Correct" : "Incorrect"}
                  </p>
                )}
              </div>
            );
          })}
        </div>
        {typeof c?.marks === "number" && (
          <p className="mt-4 text-sm text-slate-600">
            Section marks:{" "}
            <span className="font-semibold text-slate-900">
              {formatMarksDisplay(c.marks)}
            </span>
          </p>
        )}
      </div>
    );

    const japanese = (
      <div key="jp" className="">
        <h3 className="text-lg font-bold text-slate-900 sm:text-xl print:break-after-avoid">
          {APTITUDE_D_META.title}
        </h3>

        <div className="aptitude-test-4-grid mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3 print:grid-cols-2">
          {APTITUDE_D_META.items.map((item, idx) => {
            const sel = d?.selectedAnswer?.[item.key];
            const cor = d?.correctAnswer?.[item.key];
            const ok = answersMatch(sel, cor);
            return (
              <div
                key={item.key}
                className="flex flex-col rounded-2xl border border-slate-100 bg-slate-50/50 p-4 shadow-sm print:break-inside-avoid print:shadow-none"
              >
                <div className="flex gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-sm font-bold text-white">
                    {idx + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-semibold text-slate-900">
                      ({item.key}) {item.char}
                    </p>
                    <p className="mt-1 text-xs font-medium text-slate-500">
                      Type:{" "}
                      <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-slate-700">
                        japanese
                      </span>
                    </p>
                  </div>
                </div>

                <div className="mt-4 rounded-lg border border-dashed border-slate-200 bg-slate-50/80 px-3 py-2 text-xs text-slate-600">
                  <span className="font-semibold text-slate-700">
                    Options:{" "}
                  </span>
                  integers 1–42 (see printed grid in the original test)
                </div>

                <div className="mt-4 grid flex-1 gap-3 sm:grid-cols-1">
                  <div className="rounded-xl border border-sky-200 bg-sky-50/80 p-3 ring-1 ring-sky-100">
                    <p className="text-xs font-bold uppercase tracking-wide text-sky-800">
                      Selected answer
                    </p>
                    <p className="mt-1 text-xl font-bold tabular-nums text-slate-900">
                      {formatAnswer(sel)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-3 ring-1 ring-emerald-100">
                    <p className="text-xs font-bold uppercase tracking-wide text-emerald-800">
                      Correct answer
                    </p>
                    <p className="mt-1 text-xl font-bold tabular-nums text-emerald-950">
                      {formatAnswer(cor)}
                    </p>
                  </div>
                </div>
                {!hasSelection(sel) && (
                  <p className="mt-2 text-xs text-amber-800">
                    <span className="font-semibold">Not answered.</span>
                  </p>
                )}
                {ok !== null && (
                  <p
                    className={`mt-2 text-sm font-semibold ${
                      ok ? "text-emerald-600" : "text-rose-600"
                    }`}
                  >
                    {ok ? "Correct" : "Incorrect"}
                  </p>
                )}
              </div>
            );
          })}
        </div>
        {typeof d?.marks === "number" && (
          <p className="mt-4 text-sm text-slate-600">
            Section marks:{" "}
            <span className="font-semibold text-slate-900">
              {formatMarksDisplay(d.marks)}
            </span>
          </p>
        )}
      </div>
    );

    return sectionShell(
      <>
        {a && calc}
        {b && matching}
        {c && iq}
        {d && japanese}
      </>,
    );
  };

  const sectionTitle = (key: string) =>
    key
      .replace(/([A-Z])/g, " $1")
      .replace(/Part/g, " Part")
      .replace(/^./, (s) => s.toUpperCase())
      .trim();

  const sectionCardClass =
    "rounded-3xl border border-slate-100/80 bg-white p-5 shadow-lg shadow-slate-200/40 sm:p-8 print:overflow-visible print:shadow-none";

  const renderScreeningBody = () => {
    if (!screening) return null;

    const skipKeys = new Set([
      "id",
      "candidateId",
      "createDateTime",
      "lastUpdatedDateTime",
      "listening",
      "writing",
    ]);

    const parts: React.ReactNode[] = [];
    let aptitudeRendered = false;

    for (const [key, value] of Object.entries(screening)) {
      if (skipKeys.has(key)) continue;

      if (key.startsWith("aptitude_")) {
        if (!aptitudeRendered) {
          const suite = renderAptitudeSuite(screening);
          if (suite) parts.push(<div key="aptitude-suite">{suite}</div>);
          aptitudeRendered = true;
        }
        continue;
      }

      if (value === null || value === undefined) {
        parts.push(
          <section key={key} className={sectionCardClass}>
            <h3 className="text-lg font-semibold text-slate-900">
              {sectionTitle(key)}
            </h3>
            <p className="mt-2 text-sm text-slate-500">Not attempted.</p>
          </section>,
        );
        continue;
      }

      if (Array.isArray(value)) {
        const items = value as QuestionItem[];
        parts.push(
          <section key={key} className={sectionCardClass}>
            <div className="mb-6 border-b border-slate-100 pb-4">
              <h3 className="text-xl font-bold text-slate-900">
                {sectionTitle(key)}
              </h3>
            </div>
            <div className="space-y-4">
              {items.length === 0 ? (
                <p className="text-sm text-slate-500">No items.</p>
              ) : (
                items.map((item, i) => {
                  const o = item as Record<string, unknown>;
                  if (
                    o &&
                    typeof o === "object" &&
                    "answer" in o &&
                    !("question" in o)
                  ) {
                    return (
                      <div
                        key={`${key}-${i}`}
                        className="rounded-2xl border border-slate-100 bg-slate-50/80 p-4"
                      >
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                          Recording {i + 1}
                        </p>
                        <p className="mt-2 text-sm text-slate-900 whitespace-pre-wrap">
                          {formatAnswer(o.answer)}
                        </p>
                        {typeof o.marks === "number" && (
                          <p className="mt-2 text-xs text-slate-500">
                            Marks: {formatMarksDisplay(o.marks)}
                          </p>
                        )}
                      </div>
                    );
                  }
                  return renderQuestionRow(item, i);
                })
              )}
            </div>
          </section>,
        );
        continue;
      }

      if (
        typeof value === "object" &&
        value !== null &&
        ("correctAnswer" in value || "selectedAnswer" in value)
      ) {
        parts.push(
          <section key={key} className={sectionCardClass}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {sectionTitle(key)}
            </h3>
            <p className="text-sm text-slate-500">
              Structured block (unexpected here).
            </p>
          </section>,
        );
        continue;
      }

      if (typeof value === "object" && value !== null && "answer" in value) {
        const w = value as { answer?: string; marks?: number };
        parts.push(
          <section key={key} className={sectionCardClass}>
            <h3 className="text-xl font-bold text-slate-900">
              {sectionTitle(key)}
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-slate-700 whitespace-pre-wrap">
              {w.answer ?? "—"}
            </p>
            {typeof w.marks === "number" && (
              <p className="mt-4 text-xs font-medium text-slate-500">
                Marks: {formatMarksDisplay(w.marks)}
              </p>
            )}
          </section>,
        );
        continue;
      }
    }

    return <div className="space-y-8">{parts}</div>;
  };

  if (!candidateId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-md text-center">
          <p className="text-slate-700">Missing candidate ID.</p>
          <Link
            to="/placement/dashboard"
            className="mt-4 inline-block font-medium text-rose-600 hover:text-rose-700"
          >
            Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-2 border-rose-500 border-t-transparent" />
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 print:min-h-0 print:bg-white">
      <style>
        {`
          @media print {
            html,
            body {
              height: auto !important;
              overflow: visible !important;
            }
            /* Root size only — avoid * { font-size } which breaks flex/grid in print */
            .placement-result-print {
              font-size: 9px !important;
              line-height: 1.35 !important;
              overflow: visible !important;
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
            .placement-result-print * {
              font-size: inherit !important;
              line-height: inherit !important;
            }
            .placement-result-print h1 {
              font-size: 12px !important;
              line-height: 1.2 !important;
            }
            .placement-result-print h2 {
              font-size: 11px !important;
            }
            .placement-result-print h3 {
              font-size: 10px !important;
            }
            .placement-result-print img {
              max-height: 140px !important;
              max-width: 100% !important;
              width: auto !important;
              height: auto !important;
              object-fit: contain !important;
            }
            /* Aptitude Test 2 (matching): smaller figures in print / Save as PDF */
            .placement-result-print .aptitude-match-q-img {
              max-height: 80px !important;
            }
            .placement-result-print .aptitude-match-opt-img {
              max-height: 48px !important;
            }
            /* Aptitude Test 4 (Japanese): avoid cards splitting across pages */
            .placement-result-print .aptitude-test-4-grid > * {
              break-inside: avoid !important;
              page-break-inside: avoid !important;
            }
            .placement-result-print .flex,
            .placement-result-print .inline-flex {
              min-width: 0;
            }
            .placement-result-print .grid {
              break-inside: auto;
              page-break-inside: auto;
            }
            .placement-result-questions {
              display: block !important;
              overflow: visible !important;
              break-inside: auto !important;
              page-break-inside: auto !important;
            }
            .placement-result-print section,
            .placement-result-print .rounded-2xl,
            .placement-result-print .rounded-3xl,
            .placement-result-print .rounded-xl {
              box-decoration-break: clone;
              -webkit-box-decoration-break: clone;
            }
          }
        `}
      </style>
      <nav className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/90 backdrop-blur-md print:hidden">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-3 sm:gap-6">
            <Link
              to="/placement/dashboard"
              className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
            >
              <span aria-hidden>←</span> Dashboard
            </Link>
            {userName && (
              <span className="text-xs text-slate-500 sm:text-sm">
                Signed in as{" "}
                <span className="font-medium text-slate-700">{userName}</span>
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 sm:justify-end">
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-500"
            >
              <Printer className="h-4 w-4 shrink-0" aria-hidden />
              Print / Save as PDF
            </button>
            <img
              src="https://res.cloudinary.com/dureqzdco/image/upload/v1735905040/Image/r1u63eh2cz9nt9kcocr2.png"
              alt=""
              className="h-8 w-auto self-start sm:self-center sm:h-9"
            />
          </div>
        </div>
      </nav>

      <div className="placement-result-print mx-auto max-w-auto px-4 py-6 sm:px-6 sm:py-10 lg:px-8 print:py-4 print:max-w-none">
        {dataLoading && (
          <div className="flex justify-center py-20">
            <div className="h-12 w-12 animate-spin rounded-full border-2 border-rose-500 border-t-transparent" />
          </div>
        )}

        {error && !dataLoading && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-rose-900 shadow-sm">
            {error}
          </div>
        )}

        {!dataLoading && !error && (
          <>
            <header className="placement-result-summary relative mb-8 overflow-hidden rounded-3xl border border-slate-100 bg-white p-6 shadow-xl shadow-slate-200/50 sm:p-10 print:mb-6 print:break-inside-avoid print:overflow-visible print:shadow-none">
              <div className="relative">
                <p className="text-xs font-semibold uppercase tracking-widest text-rose-600">
                  Screening result
                </p>
                <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                  {marks?.name ?? "Candidate result"}
                </h1>
                {marks && (
                  <div className="mt-4 flex flex-wrap gap-3 text-sm text-slate-600">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1">
                      {marks.phoneNo}
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1">
                      {marks.education}
                    </span>
                  </div>
                )}
                {marks && (
                  <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                    {[
                      {
                        label: "Total",
                        value: formatMarksDisplay(marks.totalMarks),
                        accent: true,
                      },
                      {
                        label: "Logical",
                        value: formatMarksDisplay(marks.logicalTotal),
                      },
                      {
                        label: "Memorization",
                        value: formatMarksDisplay(marks.memorizationTotal),
                      },
                      {
                        label: "Aptitude",
                        value: formatMarksDisplay(marks.aptitudeTotal),
                      },
                    ].map((cell) => (
                      <div
                        key={cell.label}
                        className={`rounded-2xl p-4 text-center ring-1 ${
                          cell.accent
                            ? "bg-gradient-to-br from-rose-500 to-pink-600 text-white ring-rose-400/30"
                            : "bg-slate-50 text-slate-900 ring-slate-100"
                        }`}
                      >
                        <div
                          className={`text-xs font-medium uppercase tracking-wide ${
                            cell.accent ? "text-rose-100" : "text-slate-500"
                          }`}
                        >
                          {cell.label}
                        </div>
                        <div
                          className={`mt-1 text-xl font-bold tabular-nums sm:text-2xl ${
                            cell.accent ? "text-white" : "text-slate-900"
                          }`}
                        >
                          {cell.value}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {!marks && screening && (
                  <p className="mt-6 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-amber-100">
                    Marks summary unavailable for this candidate. Questions are
                    shown below.
                  </p>
                )}
              </div>
            </header>

            <section
              className="placement-result-questions mt-8 print:mt-6"
              aria-labelledby="placement-result-questions-heading"
            >
              <h2
                id="placement-result-questions-heading"
                className="mb-6 text-xl font-bold text-slate-900 print:mb-4 print:border-b print:border-slate-200 print:pb-3"
              >
                Questions
              </h2>
              {renderScreeningBody()}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default PlacementResult;
